import { api, typesenseApi, usersApiSlice } from "@/services/api";
import fakeCommentsReducer from "@/store/tasks/commentsSlice";
import fakeFilesReducer from "@/store/tasks/filesSlice";
import {
  AnyAction,
  Dispatch,
  Middleware,
  MiddlewareAPI,
  combineReducers,
  configureStore,
  isRejectedWithValue,
} from "@reduxjs/toolkit";
import CategoriesReducer from "./category/CategoriesSlices";
import certificatesReducer from "./certificates/CertificateSlices";
import eventsOfNewsReducer from "./event/EventSlices";
import inboxReducer from "./inbox/InboxSlices";
import { knowledgeBaseReducer } from "./knowledge/knownledgeBaseSlice";
import librariesSlices from "./library/librariesSlices";
import articleOfNewsReducer from "./news/NewsSlices";
import toggleSidebarReducer from "./sidebar/toggleSidebarSlice";
import { languageReducer } from "./translations";
import usersReducer from "./users/usersSlice";
import usersProfileReducer from "./profile/user/UsersSlices";
import tenantReducer, {
  TenantsType,
  selectTenant,
  shouldSelectTenantAction,
} from "./tenants/tenantsSlice";
import KanbanReducer from "./tasks/kanbanSlice";
import chatPopupReducer from "./chat-popup";

import { PERSIST, persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { getActiveAppAccountId } from "@/config/clerkConfig";
import { tenantsApi } from "./tenants/endpoints";
import { NonEmptyArray, decideMultiTenancy } from "@/utils/auth/tenants";
import { TenantType, TenantTypeEnum } from "./tenants/endpoints/tenants";
import { themeReducer } from "@/theme/theme-reducer.ts";

export const persistConfig = {
  key: "tenant",
  storage,
};

export const persistNavbarConfig = {
  key: "navbar",
  storage,
};

const persistedTenantReducer = persistReducer(persistConfig, tenantReducer);

const persistedNavBarReducer = persistReducer(persistNavbarConfig, toggleSidebarReducer);

// Extracted async logic
const handleTenantNotFoundErrorAsync = async (
  action: AnyAction,
  myApi: MiddlewareAPI<Dispatch<AnyAction>, any>
) => {
  const {
    payload: { status, data },
    meta,
  } = action;

  // Transient failures (network drop, timeout, 408/429/5xx — typically a
  // backend redeploy) must NOT destroy the session. The boot flow renders a
  // recoverable ServiceUnavailable screen that refetches until the backend
  // answers again. Only definitive auth failures fall through to logout.
  const isTransientFailure =
    status === "FETCH_ERROR" ||
    status === "TIMEOUT_ERROR" ||
    status === "PARSING_ERROR" ||
    (typeof status === "number" && (status >= 500 || status === 408 || status === 429));

  if (isTransientFailure) {
    return;
  }

  if (meta.arg.endpointName === "getTenantsList") {
    // cannot get tenant (definitive failure — e.g. 401/403/404)
    location.pathname = "logout-session";
    return;
  }
  if (status === 401 && data.errorCode === "user_not_found") {
    const {
      tenant: { tenantId },
    } = myApi.getState() as { tenant: TenantsType };

    const tenantsList = await store
      .dispatch(tenantsApi.endpoints.getTenantsList.initiate(undefined, { forceRefetch: true }))
      .unwrap();

    const acceptedTenants = tenantsList?.filter(tenant => tenant.isInvitationAccepted);

    const userId = getActiveAppAccountId();
    const currentTenant = acceptedTenants?.find(t => t.id === tenantId);

    // If current tenant is inactive, try to find an active one
    if (currentTenant && !currentTenant.isActive) {
      const activeTenants = acceptedTenants?.filter(t => t.isActive) || [];

      if (activeTenants.length > 0) {
        const tenantWithHighestPlan = activeTenants.reduce((max, curr) => {
          const currMaxPlan = curr.licenses.reduce((acc, plan) => {
            if (plan > acc) {
              return plan;
            }
            return acc;
          });

          const maxPlan = max.licenses.reduce((acc, plan) => {
            if (plan > acc) {
              return plan;
            }
            return acc;
          });

          return currMaxPlan > maxPlan ? curr : max;
        });
        store.dispatch(selectTenant({ tenantId: tenantWithHighestPlan.id, userId }));
        location.pathname = "/";
        return;
      }
    }

    location.pathname = "logout-session";
    return;
  }
  if (status === 401 && data.errorCode === "tenant_header_not_found") {
    const { tenant } = myApi.getState() as { tenant: TenantsType };
    const userId = getActiveAppAccountId();

    if (tenant.userId && tenant.userId !== userId) {
      // a mismatch between msal and local cache
      location.pathname = "logout-session";
      return;
    }

    if (!tenant.tenantsList) {
      const tenantsList: TenantType[] | undefined = (
        await myApi.dispatch(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          tenantsApi.endpoints.getTenantsList.initiate(undefined, { forceRefetch: true })
        )
      ).data;

      const tenantsListRetrieved = tenantsList?.filter(
        tenant => tenant.isInvitationAccepted && tenant.isActive
      );

      if (tenantsListRetrieved && tenantsListRetrieved.length > 0) {
        const { shouldSelectTenant, selectedTenant } = decideMultiTenancy(
          tenantsListRetrieved as NonEmptyArray<TenantType>
        );

        if (shouldSelectTenant) {
          // multiple enterprise and business tenants
          myApi.dispatch(
            shouldSelectTenantAction({
              shouldSelectTenant: true,
              tenantsList: selectedTenant as TenantType[],
            })
          );
          return;
        } else {
          // single basic or business or enterprise tenant
          myApi.dispatch(
            shouldSelectTenantAction({
              shouldSelectTenant: false,
              tenantsList: Array.isArray(selectedTenant)
                ? (selectedTenant as TenantType[])
                : ([selectedTenant] as TenantType[]),
            })
          );
          myApi.dispatch(
            selectTenant({
              // decideMultiTenancy returns an ARRAY for the owner branches
              // (even when it has only 1 element) and a bare OBJECT for the
              // "single Personal tenant" branch. The prior length>1 gate
              // treated a length-1 array as an object and dispatched
              // tenantId=undefined, leaving Redux empty and the app
              // spinning forever on RootWrapper. Use Array.isArray so a
              // length-1 array is still handled as an array.
              tenantId: Array.isArray(selectedTenant)
                ? ((selectedTenant as TenantType[]).find(
                    tenant => tenant.tenantType === TenantTypeEnum.Commercial
                  )?.id ?? (selectedTenant as TenantType[])[0]?.id)
                : (selectedTenant as TenantType).id,
            })
          );
          return;
        }
      } else {
        // no tenant
        location.pathname = "logout-session";
        return;
      }
    }
  }
};

const handleTenantNotFoundErrorMiddleware: Middleware = myApi => next => action => {
  if (isRejectedWithValue(action)) {
    handleTenantNotFoundErrorAsync(action, myApi)
      .then(() => {
        return next(action);
      })
      .catch(_error => {
        return next(action);
      });
  } else {
    return next(action);
  }
};

const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  [usersApiSlice.reducerPath]: usersApiSlice.reducer,
  [typesenseApi.reducerPath]: typesenseApi.reducer,
  articlesOfNews: articleOfNewsReducer,
  categories: CategoriesReducer,
  eventsOfNews: eventsOfNewsReducer,
  libraries: librariesSlices,
  inbox: inboxReducer,
  users: usersReducer,
  knowledgeBase: knowledgeBaseReducer,
  certificates: certificatesReducer,
  fakeComments: fakeCommentsReducer,
  language: languageReducer,
  theme: themeReducer,
  fakeFiles: fakeFilesReducer,
  toggleSidebar: persistedNavBarReducer,
  usersProfile: usersProfileReducer,
  tenant: persistedTenantReducer,
  kanban: KanbanReducer,
  chatPopup: chatPopupReducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [PERSIST],
      },
    }).concat([
      api.middleware,
      handleTenantNotFoundErrorMiddleware,
      usersApiSlice.middleware,
      typesenseApi.middleware,
    ]),
});

export type RootState = ReturnType<typeof store.getState>;

export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
